import React, { useState, useEffect, useCallback, memo } from "react";
import { Card, Select, Button, Table, Modal, Form, Input, InputNumber, Switch, message, Space, Tag, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  getPeriodicals,
  getPeriodicalById,
  addPeriodical,
  updatePeriodical,
  deletePeriodical,
  getOccurrences,
  getAllocationPreferences,
  getAllStatus,
} from "./periodicalApi";

const { Option } = Select;
const { confirm } = Modal;

// Known gap: periodicals can carry a per-type set of custom "reporting
// fields" (data_type_id/label/is_mandatory) in the real API's create/update
// payload, but the only GET endpoint with that name
// (managed_periodical_reporting_fields) turned out to read back something
// else entirely on inspection — facility/contract-scoped reporting field
// VALUES tied to a specific scheduled occurrence, not the periodical
// template's own field definitions. Sending an empty reporting_fields array
// (valid per the schema, which doesn't require it) until that's resolved.
const PeriodicalFormModal = ({ visible, editing, occurrences, allocationPreferences, statuses, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (!visible) return;
    if (!editing) {
      form.setFieldsValue({
        name: "",
        description: "",
        is_default_seperately_charge: false,
        default_frequency_type_id: undefined,
        default_frequency_count: 1,
        allocation_preference_id: undefined,
        status: 1,
      });
      return;
    }
    // The list row is flatter than the single-record endpoint (no
    // description) — fetch full detail for editing. That endpoint inner-joins
    // against contract assignments, so a periodical with none yet can come
    // back empty; fall back to the list row's fields in that case.
    (async () => {
      setLoadingDetail(true);
      const { error, result } = await getPeriodicalById(editing.id);
      const detail = !error && Array.isArray(result?.result) ? result.result[0] : null;
      const source = detail || editing;
      form.setFieldsValue({
        name: source.name || "",
        description: source.description || "",
        is_default_seperately_charge: !!source.is_default_seperately_charge,
        default_frequency_type_id: source.default_frequency_type_id,
        default_frequency_count: source.default_frequency_count || 1,
        allocation_preference_id: source.allocation_preference_id,
        status: source.status,
      });
      setLoadingDetail(false);
    })();
  }, [visible, editing, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        name: values.name,
        description: values.description || "",
        is_default_seperately_charge: values.is_default_seperately_charge ? 1 : 0,
        default_frequency_type_id: values.default_frequency_type_id,
        default_frequency_count: values.default_frequency_count,
        allocation_preference_id: values.allocation_preference_id,
        // Required by the API on add as well as edit (status is NOT NULL);
        // new periodicals default to Active, as in the old Periodical app.
        status: values.status,
        icon_hash: "", // no icon upload here; the API expects the field, as the old app sent it
        reporting_fields: [],
      };
      const { error, result } = editing
        ? await updatePeriodical({ ...payload, id: editing.id })
        : await addPeriodical(payload);
      if (error) message.error(result?.message || "Failed to save.");
      else {
        message.success(editing ? "Periodical updated." : "Periodical added.");
        onSaved();
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editing ? "Edit Periodical" : "Add Periodical"} visible={visible} onCancel={onClose} onOk={handleOk} confirmLoading={saving || loadingDetail} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Name" rules={[{ required: true, message: "Please enter the name." }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="default_frequency_type_id" label="Default Frequency" rules={[{ required: true, message: "Please select a default frequency." }]}>
          <Select showSearch optionFilterProp="children" placeholder="Select a frequency">
            {occurrences.map((o) => <Option key={o.id} value={o.id}>{o.name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="default_frequency_count" label="Default Frequency Count" rules={[{ required: true, message: "This field is required." }]}>
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="allocation_preference_id" label="Allocation Preference" rules={[{ required: true, message: "Please select an allocation preference." }]}>
          <Select showSearch optionFilterProp="children" placeholder="Select an allocation preference">
            {allocationPreferences.map((a) => <Option key={a.id} value={a.id}>{a.name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="is_default_seperately_charge" label="Separately Charged" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="status" label="Status" rules={[{ required: true, message: "Please select a status." }]}>
          <Select placeholder="Select status">
            {statuses.map((s) => <Option key={s.id} value={s.id}>{s.name}</Option>)}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const Periodicals = () => {
  const [rows, setRows] = useState([]);
  const [occurrences, setOccurrences] = useState([]);
  const [allocationPreferences, setAllocationPreferences] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formModal, setFormModal] = useState({ visible: false, editing: null });

  const load = useCallback(async () => {
    setLoading(true);
    const [rowsRes, occRes, allocRes, statusRes] = await Promise.all([
      getPeriodicals(),
      getOccurrences(),
      getAllocationPreferences(),
      getAllStatus(),
    ]);
    setRows(!rowsRes.error && Array.isArray(rowsRes.result?.result) ? rowsRes.result.result : []);
    setOccurrences(!occRes.error && Array.isArray(occRes.result?.result) ? occRes.result.result : []);
    setAllocationPreferences(!allocRes.error && Array.isArray(allocRes.result?.result) ? allocRes.result.result : []);
    setStatuses(!statusRes.error && Array.isArray(statusRes.result?.result) ? statusRes.result.result : []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const allocationName = (id) => allocationPreferences.find((a) => a.id === id)?.name || id;
  const statusMeta = (id) => statuses.find((s) => s.id === id);

  const handleDelete = useCallback((row) => {
    confirm({
      title: "Delete periodical",
      content: `Delete "${row.name}"?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const { error, result } = await deletePeriodical(row.id);
        if (error) message.error(result?.message || "Failed to delete.");
        else { message.success("Deleted."); load(); }
      },
    });
  }, [load]);

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Default Frequency", dataIndex: "default_frequency_name", key: "default_frequency_name" },
    { title: "Allocation Preference", dataIndex: "allocation_preference_id", key: "allocation_preference_id", render: allocationName },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (id) => <Tag>{statusMeta(id)?.name || id}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Space>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => setFormModal({ visible: true, editing: row })} type="primary" aria-label="Edit" /></Tooltip>
          <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} aria-label="Delete" /></Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ textAlign: "right", marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormModal({ visible: true, editing: null })}>Add Periodical</Button>
      </div>
      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: "No periodicals defined yet." }}
        />
      </Card>

      <PeriodicalFormModal
        visible={formModal.visible}
        editing={formModal.editing}
        occurrences={occurrences}
        allocationPreferences={allocationPreferences}
        statuses={statuses}
        onClose={() => setFormModal({ visible: false, editing: null })}
        onSaved={load}
      />
    </div>
  );
};

export default memo(Periodicals);
